import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import {
  selectBusinessInventoryList,
  selectBusinessInventoryListLoading,
  selectCurrentBusiness,
  selectIsDisabled,
} from 'redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteInventoryToProduct,
  getBusinessInventoryList,
} from 'redux/actions/inventory/inventory';
import { confirm } from 'components/MenuNew/ConfirmModal';
import { getProductById } from 'redux/actions/Menu/products';
import { SET_FORM_PRODUCT } from 'redux/actions/types';
import { useInventoryConfig } from 'components/MenuNew/FormView/Inventory/InventoryConfigContext';
import useQuery from 'hooks/useQuery';

const inventorySchema = yup.array().of(
  yup.object().shape({
    uuid: yup.string().required('SKU is required'),
    name: yup.string(),
    qty: yup
      .number()
      .min(0, 'Quantity must be non-negative')
      .required('Quantity is required'),
  })
);

const formSchema = yup.object().shape({
  inventoryItems: inventorySchema,
});

const defaultValues = {
  inventoryItems: [{ uuid: '', qty: 0 }],
};

const useInventoryHook = (productFormMethods) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const query = useQuery();
  const cid = query.get('cid');
  const inventoryConfig = useInventoryConfig();
  const currentBusiness = useSelector(selectCurrentBusiness);
  const businessInventoryList = useSelector(selectBusinessInventoryList);
  const businessInventoryListLoading = useSelector(
    selectBusinessInventoryListLoading
  );
  const isDisabled = useSelector(selectIsDisabled);
  const productId = productFormMethods.getValues()?._id;
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  // Drive loading off the actual fetch state instead of a fixed timer so the
  // inventory UI appears as soon as the data is ready.
  const isLoading = businessInventoryListLoading || !initialFetchDone;

  // Reactively track the selected item's inventory. `getValues()` is not
  // reactive, so switching items (which resets the parent form) would not
  // re-run the sync effect below and the list would keep the old item's data.
  const watchedInventory = useWatch({
    control: productFormMethods.control,
    name: 'inventory',
  });

  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Initialize form with default values
  const inventoryMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: defaultValues,
    mode: 'all',
  });

  const { control, reset } = inventoryMethods;

  const inventoryItems = useFieldArray({
    control,
    name: 'inventoryItems',
  });

  const {
    fields: inventoryFields,
    remove: removeInventory,
    append: appendInventory,
    replace: replaceInventory,
  } = inventoryItems;

  const handleClose = () => {
    setOpen(false);
    setEditingItem(null);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleEdit = (field) => {
    setEditingItem(field);
    setOpen(true);
  };

  const getProduct = async (productId) => {
    // When a host injects a custom refetch (e.g. POS menu items use a
    // different endpoint), use it; otherwise fall back to the regular
    // menu product fetch.
    if (inventoryConfig?.refetchProduct) {
      const res = await inventoryConfig.refetchProduct(productId);
      if (res) {
        productFormMethods.reset(res);
      }
      return res;
    }
    await dispatch(getProductById(productId, cid)).then((res) => {
      productFormMethods.reset(res);
      dispatch({ type: SET_FORM_PRODUCT, payload: res });
    });
  };

  useEffect(() => {
    const inventory = watchedInventory;
    const inventoryItems = inventory?.map((item) => {
      const businessInventory = businessInventoryList?.find(
        (business) => business.uuid === item.uuid
      );
      return {
        uuid: item.uuid,
        name: businessInventory?.name,
        qty: item.qty,
        isMain: item?.isMain,
      };
    });
    if (inventory?.length > 0) {
      replaceInventory(inventoryItems);
    } else {
      reset(defaultValues);
    }
  }, [watchedInventory, businessInventoryList]);

  useEffect(() => {
    let cancelled = false;
    const loadBusinessInventory = async () => {
      if (!currentBusiness?.uuid) {
        if (!cancelled) setInitialFetchDone(true);
        return;
      }
      try {
        await dispatch(getBusinessInventoryList(currentBusiness.uuid));
      } catch (error) {
        console.log(error, '>>>');
      } finally {
        if (!cancelled) setInitialFetchDone(true);
      }
    };
    loadBusinessInventory();
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.uuid, dispatch]);

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const deleteInventory = async (uuid, index) => {
    const confirmed = await confirm({
      title: t('Confirm Product Inventory Deletion'),
      message: t('Are you sure you want to delete this product inventory?'),
      cancelText: t('Cancel'),
      confirmText: t('Delete'),
      confirmColor: 'primary',
      background: 'rgba(226, 21, 8, 1)',
    });
    if (confirmed) {
      try {
        // POS menu items manage inventory on the POS item itself, not via the
        // MMS `/products/:id/inventory` endpoints, so use the host override.
        if (inventoryConfig?.deleteInventory) {
          await inventoryConfig.deleteInventory(uuid);
        } else {
          await dispatch(deleteInventoryToProduct(productId, uuid));
        }
        getProduct(productId);
        removeInventory(index);
      } catch (error) {
        console.log(error, '>>>');
      }
    }
  };

  return {
    handleSubmit,
    inventoryFields,
    inventoryMethods,
    open,
    handleClose,
    handleOpen,
    replaceInventory,
    deleteInventory,
    handleEdit,
    editingItem,
    isLoading,
    isDisabled,
  };
};

export default useInventoryHook;
